import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Input, Select, message, Button, Tooltip } from "antd";
import CustomModal from "../../../../components/Common/CustomModal";
import {
  CLASS_SECTION_OPTIONS_BY_LEVEL,
} from "../../../../constants/classConstants";
import type { ClassData } from "../../../../types/classTypes";
import type { Subject } from "../../../../types/subjectTypes";
import { OptionType } from "components/Common/CommonSelectAntd";
import CustomTooltip from "components/Common/ui/CustomTooltip";
import { InfoCircleOutlined } from "@ant-design/icons";
import { CustomLabel } from "components/Common/ui/CustomLabel";
import { getBranchLabelFromCode, getClassLevelLabelFromCode } from "utils/schoolUtils";

interface AddEditClassModalProps {
  classData?: Partial<ClassData> | null;
  onSave: (classData: Partial<ClassData>) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  subjects: Subject[];
  classLevels: any[];
}

const AddEditClassModal: React.FC<AddEditClassModalProps> = ({
  classData,
  onSave,
  onClose,
  isOpen,
  subjects,
  classLevels,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string | undefined>();
  const [filteredClassLevels, setFilteredClassLevels] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setSelectedSection(undefined);
    }
  }, [isOpen, form]);

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        name: classData?.name || "",
        code: classData?.code || "",
        section: classData?.classLevel?.schoolCycle,
        classLevelId: classData?.classLevel?.id,
        branchId: classData?.branch?.id,
        // classSubjectCoefficient:
        //   classData?.classSubjectCoefficient?.map((item) => ({
        //     subjectId: item.subject?.id || item.subjectId,
        //     coefficient: item.coefficient,
        //   })) || [],
      });
      setSelectedSection(classData?.classLevel?.schoolCycle);
      setFilteredClassLevels(classData?.classLevel?.schoolCycle ? classLevels.filter((level) => level.schoolCycle === classData?.classLevel?.schoolCycle) : classLevels);
    }
  }, [isOpen, classData, form]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const { section, ...valuesWithoutSection } = values;
      await onSave({ ...(classData || {}), ...valuesWithoutSection });
      message.success(
        classData?.id
          ? t("class_updated_successfully")
          : t("class_added_successfully")
      );
      onClose();
    } catch {
      message.error(t("please_fill_all_required_fields_correctly"));
    } finally {
      setLoading(false);
    }
  };

  const toOptions = (list: Subject[]): OptionType[] =>
    list.map((s) => ({ value: s.id, label: s.name }));

  const onChangeSection = (value: string) => {
    setSelectedSection(value);
    setFilteredClassLevels(classLevels.filter((level) => level.schoolCycle === value));
    form.setFieldsValue({ classLevelId: undefined });
    form.setFieldsValue({ branchId: undefined });
    setBranches([]);
  };

  const onChangeClassLevel = (value: string) => {
    const classLevel = classLevels.find((level) => level.id === value);
    form.setFieldsValue({ branchId: undefined });
    setBranches([]);


    if (classLevel?.branch?.length > 0) {
      classLevel?.branch?.forEach((branch: any) => {
        setBranches((prevBranches: any) => [...prevBranches, { id: branch.id, name: branch.name , labelKey: branch.labelKey }]);
      });
    }
  };

  return (
    <CustomModal
      title={classData?.id ? t("edit_class") : t("add_class")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={classData?.id ? t("save_changes") : t("add_class")}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t("class_name")}
          name="name"
          rules={[{ required: true, message: t("please_enter_a_class_name") }]}
        >
          <Input placeholder={t("enter_class_name")} />
        </Form.Item>

        <CustomLabel label={t("class_code")} tooltip={t("tooltips.class_code")} required />
        <Form.Item
          label={false}
          name="code"
          rules={[{ required: true, message: t("validation.please_enter_a_class_code") }]}
        >
          <Input placeholder={t("enter_class_code")} />
        </Form.Item>

        {!classData?.id && (<>

          <Form.Item
            label={t("class_section")}
            name="section"
            rules={[{ required: true, message: t("validation.please_select_a_section") }]}
          >
            <Select
              placeholder={t("select_a_section")}
              onChange={(value) => {
                onChangeSection(value);
              }}
            >
              {CLASS_SECTION_OPTIONS_BY_LEVEL(t).map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t("class_level")}
            name="classLevelId"
            rules={[
              { required: true, message: t("validation.please_select_a_class_level") },
            ]}
          >
            <Select placeholder={t("select_a_level")} disabled={!selectedSection} onChange={(value) => onChangeClassLevel(value)}>
              {filteredClassLevels.map((level) => (
                <Select.Option key={level.id} value={level.id}>
                  {getClassLevelLabelFromCode(level.labelKey)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {branches.length > 0 && (
            <Form.Item
              label={t("branch")}
              name="branchId"
              rules={[{ required: true, message: t("validation.please_select_a_branch") }]}
            >
              <Select placeholder={t("branch")}>
                {branches.map((branch) => (
                  <Select.Option key={branch.id} value={branch.id}>
                    {getBranchLabelFromCode(branch.labelKey , t)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </>)}
      </Form>
    </CustomModal>
  );
};

export default AddEditClassModal;
